import type { MarketPoint } from "../market-data/types";

export type GraphLayout = {
  x0: number;
  y0: number;
  x1: number;
  y1: number;
};

export type ScaleState = {
  prev: number;
  halfRange: number;
  yMin: number;
  yMax: number;
};

export const DEFAULT_GRAPH: GraphLayout = { x0: 156, y0: 235, x1: 988, y1: 684 };

export const scaleConfig = {
  minHalfRange: 28,
  openPadding: 1.16,
  visiblePadding: 1.14,
  extraPadding: 5,
  smooth: 0.105
};

export function computeInitialHalfRange(open: number, previousClose: number): number {
  const openDistance = Math.abs(open - previousClose);
  const minimum = Math.max(scaleConfig.minHalfRange, previousClose * 0.0035);
  return Math.max(minimum, openDistance * scaleConfig.openPadding + scaleConfig.extraPadding);
}

export function createInitialScale(open: number, previousClose: number): ScaleState {
  const halfRange = computeInitialHalfRange(open, previousClose);
  return {
    prev: previousClose,
    halfRange,
    yMin: previousClose - halfRange,
    yMax: previousClose + halfRange
  };
}

export function buildTargetScale(points: MarketPoint[], visibleCount: number, previousClose: number, initialHalfRange: number): ScaleState {
  const visible = points.slice(0, visibleCount).map((point) => point.y);
  const maxDistance = Math.max(
    Math.abs(Math.max(...visible, previousClose) - previousClose),
    Math.abs(Math.min(...visible, previousClose) - previousClose)
  );
  const halfRange = Math.max(initialHalfRange, maxDistance * scaleConfig.visiblePadding + scaleConfig.extraPadding);
  return { prev: previousClose, halfRange, yMin: previousClose - halfRange, yMax: previousClose + halfRange };
}

export function stepScale(current: ScaleState, target: ScaleState): ScaleState {
  const halfRange = current.halfRange + (target.halfRange - current.halfRange) * scaleConfig.smooth;
  return { prev: target.prev, halfRange, yMin: target.prev - halfRange, yMax: target.prev + halfRange };
}

export function xToPx(x: number, graph: GraphLayout = DEFAULT_GRAPH): number {
  return graph.x0 + x * (graph.x1 - graph.x0);
}

export function yToPx(y: number, scale: ScaleState, graph: GraphLayout = DEFAULT_GRAPH): number {
  const graphMidY = (graph.y0 + graph.y1) / 2;
  const normalized = (y - scale.prev) / scale.halfRange;
  return graphMidY - normalized * ((graph.y1 - graph.y0) / 2);
}
